import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { CampoControlado, useCamposPersonalizados } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { useToast } from '../../componentes/toast';
import { cabecalhoEmpresa, ErroRequisicao, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { precisaEscolherEmpresa } from '../../nucleo/empresa/empresa-do-registro';
import { AtalhoCamposDaTela, useCamposDaTela } from '../../nucleo/campos-tela';
import { useSessao } from '../../nucleo/sessao';
import { useDepositos } from './comum';
import { ItensEditor, linhasParaApi } from './ItensEditor';
import { hojeLocal, invalidarCompras, novaLinha, type FornecedorDetalhe, type LinhaItem, type PedidoDetalhe } from './tipos';
import { nomeCadastro, nomePessoa } from '../../nucleo/formato';

const soma = (data: string, dias: number) => {
  const d = new Date(`${data}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Drawer de novo pedido (M10.1, tool pedido_compra.criar) ou de edição do rascunho. Escolher o fornecedor preenche a
 * condição de pagamento e o prazo padrão dele (M10.2) e sugere o último preço pago em cada produto (M10.4).
 * O id nasce com a tela: reenviar o formulário devolve o mesmo pedido, nunca um segundo.
 */
export function FormPedido({ aberto, pedido, aoFechar, aoSalvar }: { aberto: boolean; pedido?: PedidoDetalhe | null; aoFechar: () => void; aoSalvar?: (id: string) => void }) {
  if (!aberto) return null;
  return <Corpo pedido={pedido ?? null} aoFechar={aoFechar} aoSalvar={aoSalvar} />;
}

function Corpo({ pedido, aoFechar, aoSalvar }: { pedido: PedidoDetalhe | null; aoFechar: () => void; aoSalvar?: (id: string) => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const depositos = useDepositos();
  const extrasDefs = useCamposPersonalizados('compras.pedido');
  // Lote de 19/10 (item 9): campos que o workspace escondeu ou tornou obrigatórios (Configurações > Campos das telas).
  const tela = useCamposDaTela('compras.pedido');
  const [novoId] = useState(() => crypto.randomUUID());
  const [fornecedorId, setFornecedorId] = useState<string | null>(pedido?.fornecedor_id ?? null);
  const [condicao, setCondicao] = useState(pedido?.condicao_pagamento ?? '');
  const [prazo, setPrazo] = useState(pedido?.prazo_entrega ?? '');
  const [depositoId, setDepositoId] = useState(pedido?.deposito_id ?? '');
  const [observacoes, setObservacoes] = useState(pedido?.observacoes ?? '');
  const [extras, setExtras] = useState<Record<string, unknown>>(pedido?.extras ?? {});
  const [linhas, setLinhas] = useState<LinhaItem[]>(
    pedido
      ? pedido.itens_detalhe.map((i) => ({
          ...novaLinha(),
          produto_id: i.produto_id,
          produto_rotulo: `${i.produto_codigo ?? ''} ${nomeCadastro(i.produto_nome)}`.trim(),
          quantidade: String(i.quantidade).replace('.', ','),
          preco: i.preco_unitario_centavos,
        }))
      : [novaLinha()],
  );
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});

  async function escolherFornecedor(id: string | null) {
    setFornecedorId(id);
    if (!id || pedido) return;
    try {
      const f = await get<FornecedorDetalhe>(`/compras/fornecedores/${id}`);
      if (f.condicao_pagamento) setCondicao((atual) => atual || f.condicao_pagamento!);
      if (f.prazo_entrega_dias !== null) setPrazo((atual) => atual || soma(hojeLocal(), f.prazo_entrega_dias!));
    } catch {
      // Sem condição cadastrada ou sem permissão de leitura: os campos ficam como estão.
    }
  }

  // V2 verticais: pedido novo sem empresa na aba (sem filtro, várias permitidas) pede a empresa.
  const escolherEmpresa = !pedido && precisaEscolherEmpresa(s.empresas);
  const [empresaRegistro, setEmpresaRegistro] = useState('');

  const enviar = useMutation({
    mutationFn: async () => {
      if (escolherEmpresa && !empresaRegistro) throw new Error(tn('empresa.escolha'));
      if (!fornecedorId) throw new Error(t('erros.escolhaFornecedor'));
      const itens = linhasParaApi(linhas, { preco: true, prazo: false }, { semItens: t('erros.semItens'), incompleto: t('erros.itemIncompleto') });
      const corpo = {
        fornecedor_id: fornecedorId,
        condicao_pagamento: condicao.trim() || null,
        prazo_entrega: prazo || null,
        deposito_id: depositoId || null,
        observacoes: observacoes.trim() || null,
        itens,
        extras,
      };
      if (pedido) {
        await patch(`/compras/pedidos/${pedido.id}`, corpo);
        return pedido.id;
      }
      await post('/compras/pedidos', { id: novoId, ...corpo }, { 'Idempotency-Key': novoId, ...cabecalhoEmpresa(empresaRegistro) });
      return novoId;
    },
    onSuccess: async (id) => {
      await invalidarCompras(qc);
      toast.sucesso(tn('comum.salvo'));
      aoSalvar?.(id);
      aoFechar();
    },
    onError: (e) => {
      setErros(e instanceof ErroRequisicao ? e.porCampo() : {});
      setErro(mensagemDeErro(e));
    },
  });

  const { ref: refForm } = useValidacaoLocal(erros, erro);

  function submeter(ev: FormEvent) {
    ev.preventDefault();
    setErro(null);
    setErros({});
    enviar.mutate();
  }

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={pedido ? t('pedido.editarTitulo', { numero: pedido.numero }) : t('acoes.novoPedido')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-pedido" carregando={enviar.isPending}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <form ref={refForm} id="form-pedido" onSubmit={submeter} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-6">
        <AtalhoCamposDaTela telaCampos="compras.pedido" />
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-6">
            {erro}
          </p>
        )}
        {escolherEmpresa && (
          <div className="sm:col-span-6">
            <CampoControlado
              def={{ nome: 'empresa', rotulo: tn('empresa.rotulo'), tipo: 'selecao', obrigatorio: true, ajuda: tn('empresa.ajudaRegistro'), opcoes: (s.empresas?.opcoes ?? []).map((e) => ({ valor: e.id, rotulo: e.nome })) }}
              valor={empresaRegistro}
              aoMudar={(v) => setEmpresaRegistro(String(v ?? ''))}
            />
          </div>
        )}
        <div className="sm:col-span-6">
          <CampoControlado
            def={{ nome: 'fornecedor_id', rotulo: t('campos.fornecedor'), tipo: 'busca', obrigatorio: true, busca: { caminho: '/cadastros/parceiros', params: { papel: 'fornecedor' }, rotulo: (i) => String(i.nome) } }}
            valor={fornecedorId}
            rotuloAtual={nomePessoa(pedido?.fornecedor_nome)}
            aoMudar={(v) => void escolherFornecedor((v as string | null) || null)}
            erro={erros.fornecedor_id}
          />
        </div>
        {tela.visivel('condicao_pagamento') && (
          <div className="sm:col-span-2">
            <CampoControlado def={{ nome: 'condicao_pagamento', rotulo: t('campos.condicao'), tipo: 'texto', obrigatorio: tela.obrigatorio('condicao_pagamento'), placeholder: 'avista', ajuda: t('ajuda.condicaoPedido') }} valor={condicao} aoMudar={(v) => setCondicao(String(v ?? ''))} erro={erros.condicao_pagamento} />
          </div>
        )}
        {tela.visivel('prazo_entrega') && (
          <div className="sm:col-span-2">
            <CampoControlado def={{ nome: 'prazo_entrega', rotulo: t('campos.prazoEntrega'), tipo: 'data', obrigatorio: tela.obrigatorio('prazo_entrega'), ajuda: t('ajuda.prazoEntrega') }} valor={prazo} aoMudar={(v) => setPrazo(String(v ?? ''))} erro={erros.prazo_entrega} />
          </div>
        )}
        {tela.visivel('deposito_id') && (
        <div className="sm:col-span-2">
          <CampoControlado
            def={{ nome: 'deposito_id', rotulo: t('campos.deposito'), tipo: 'selecao', obrigatorio: tela.obrigatorio('deposito_id'), opcoes: (depositos.data?.itens ?? []).map((d) => ({ valor: d.id, rotulo: d.padrao ? `${d.nome} (${t('campos.padrao')})` : d.nome })), ajuda: t('ajuda.deposito') }}
            valor={depositoId}
            aoMudar={(v) => setDepositoId(String(v ?? ''))}
            erro={erros.deposito_id}
          />
        </div>
        )}
        {tela.visivel('observacoes') && (
          <div className="sm:col-span-6">
            <CampoControlado def={{ nome: 'observacoes', rotulo: t('campos.observacoes'), tipo: 'area', obrigatorio: tela.obrigatorio('observacoes') }} valor={observacoes} aoMudar={(v) => setObservacoes(String(v ?? ''))} erro={erros.observacoes} />
          </div>
        )}
        {extrasDefs.map((c) => (
          <div key={c.nome} className="sm:col-span-3">
            <CampoControlado def={c} valor={extras[c.nome.slice(7)]} aoMudar={(v) => setExtras((x) => ({ ...x, [c.nome.slice(7)]: v }))} />
          </div>
        ))}
        <ItensEditor linhas={linhas} aoMudar={setLinhas} comPreco fornecedorId={fornecedorId} moeda={s.workspace.moeda} />
      </form>
    </Drawer>
  );
}
